import { useQueryClient } from "@tanstack/react-query";
import {
  applyTheme,
  Logo,
  meUser,
  storedTheme,
  type ThemePreference,
  useMe,
} from "@wirevod/app-core";
import {
  AppShell,
  type AppShellLinkProps,
  type AppShellNavGroup,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuSeparator,
  ToastProvider,
} from "@wirevod/ui";
import { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import { logout } from "@/api/auth";
import { useSuspended } from "@/api/suspension";
import { UploadProvider } from "@/features/uploads/UploadProvider";
import styles from "./AppLayout.module.css";
import { Icons } from "./icons";
import { SuspendedNotice } from "./SuspendedNotice";

const DOCS_URL = "https://wirevod.online/docs/";

function navigation(pathname: string): AppShellNavGroup[] {
  const at = (href: string) =>
    href === "/" ? pathname === "/" : pathname === href || pathname.startsWith(`${href}/`);
  const item = (label: string, href: string, icon: React.ReactNode) => ({
    label,
    href,
    icon,
    active: at(href),
  });
  return [
    {
      items: [
        item("Overview", "/", <Icons.overview />),
        item("Assets", "/assets", <Icons.assets />),
      ],
    },
    { label: "Developers", items: [item("API keys", "/api-keys", <Icons.key />)] },
    {
      label: "Settings",
      items: [
        item("Organization", "/settings/organization", <Icons.building />),
        item("Members", "/settings/members", <Icons.users />),
        item("Account", "/settings/account", <Icons.user />),
      ],
    },
  ];
}

const renderLink = ({ href, className, onClick, children, ...rest }: AppShellLinkProps) => (
  <Link to={href} className={className} onClick={onClick} {...rest}>
    {children}
  </Link>
);

function AccountMenu() {
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [theme, setTheme] = useState<ThemePreference>(storedTheme);
  const email = meUser(me.data)?.email ?? "";
  const initial = (email || "?").slice(0, 1).toUpperCase();

  async function signOut() {
    await logout();
    queryClient.clear();
    navigate("/login", { replace: true });
  }

  return (
    <DropdownMenu
      trigger={
        <button type="button" className={styles.account} aria-label="Account menu">
          <span className={styles.avatar} aria-hidden="true">
            {initial}
          </span>
          <span className={styles.email}>{email}</span>
          <Icons.chevronDown />
        </button>
      }
    >
      <DropdownMenuLabel>Signed in as {email}</DropdownMenuLabel>
      <DropdownMenuItem icon={<Icons.user />} onSelect={() => navigate("/settings/account")}>
        Account settings
      </DropdownMenuItem>
      <DropdownMenuItem
        icon={<Icons.book />}
        onSelect={() => window.open(DOCS_URL, "_blank", "noopener")}
      >
        Documentation
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuRadioGroup
        label="Theme"
        value={theme}
        onValueChange={(value) => {
          const next = value as ThemePreference;
          setTheme(next);
          applyTheme(next);
        }}
        options={[
          { value: "system", label: "System" },
          { value: "light", label: "Light" },
          { value: "dark", label: "Dark" },
        ]}
      />
      <DropdownMenuSeparator />
      <DropdownMenuItem icon={<Icons.signOut />} onSelect={() => void signOut()}>
        Sign out
      </DropdownMenuItem>
    </DropdownMenu>
  );
}

export function AppLayout() {
  const location = useLocation();
  const me = useMe();
  const suspendedFlag = useSuspended();
  const workspace = me.data?.tenant.name;
  if (suspendedFlag || me.data?.tenant.status === "suspended") {
    return <SuspendedNotice />;
  }

  return (
    <ToastProvider>
      <AppShell
        brand={
          <Link to="/" className={styles.brand} aria-label="WireVoD overview">
            <Logo />
          </Link>
        }
        navigation={navigation(location.pathname)}
        renderLink={renderLink}
        topBar={
          <div className={styles.topBar}>
            <span className={styles.workspace}>{workspace}</span>
            <AccountMenu />
          </div>
        }
        sidebarFooter={
          <a className={styles.docs} href={DOCS_URL} target="_blank" rel="noopener">
            <Icons.book />
            <span>Documentation</span>
            <Icons.external />
          </a>
        }
      >
        <UploadProvider>
          <div className={styles.page}>
            <Outlet />
          </div>
        </UploadProvider>
      </AppShell>
    </ToastProvider>
  );
}
